<template>
	<v-card :class="embedded ? '' : 'ma-5'" flat>
		<v-card-text :class="embedded ? 'pa-3' : ''">
			<!-- Loading State -->
			<div v-if="loading" class="d-flex flex-column justify-center align-center py-8">
				<v-progress-circular indeterminate color="cyan" />
				<span class="mt-3 text--secondary">{{ $t('loading') }}...</span>
			</div>

			<!-- Error State -->
			<v-alert
				v-else-if="error"
				type="error"
				outlined
				dense
				class="mb-0"
			>
				{{ error }}
			</v-alert>

			<!-- No SIP Users State -->
			<div v-else-if="!sipUsers.length" class="no-data-state">
				<v-icon size="64" color="grey lighten-1">mdi-phone-off</v-icon>
				<span class="no-data-text">{{ $t('yealink-config-no-sip-users') }}</span>
			</div>

			<!-- Content -->
			<div v-else class="config-content">
				<!-- SIP Users Warning -->
				<v-alert
					type="info"
					outlined
					dense
					class="mb-4 sip-warning-alert"
				>
					<div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-sm-space-between">
						<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-sip-warning') }}</span>
						<v-btn
							small
							outlined
							color="info"
							class="mt-2 mt-sm-0 ml-sm-3"
							@click="openSipPanel"
						>
							<v-icon small left>mdi-account-cog</v-icon>
							{{ $t('yealink-config-manage-sip-users') }}
						</v-btn>
					</div>
				</v-alert>

				<!-- Device Section -->
				<div class="config-section mb-4">
					<div class="section-title mb-4 d-flex align-center">
						<v-icon small class="mr-2">mdi-cellphone-link</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-device') }}</span>
					</div>
					<v-row dense>
						<!-- MAC Address (Optional) -->
						<v-col cols="12" md="6">
							<v-select
								v-if="!manualMacInput"
								v-model="macAddress"
								:items="deviceMacOptions"
								:label="$t('yealink-config-mac-address')"
								outlined
								dense
								clearable
								prepend-inner-icon="mdi-ethernet"
								hint=" "
								persistent-hint
								:rules="macRules"
								@input="onMacAddressChange"
							>
								<template #message>
									{{ $t('yealink-config-mac-hint') }}
									<a class="mac-hint-link" @click.prevent="openYealinkPanel">
										{{ $t('yealink-config-add-devices') }}
									</a>
								</template>
								<template #item="{ item }">
									<v-list-item-content>
										<v-list-item-title class="d-flex align-center">
											<span class="device-status-pill mr-2" :class="`device-status-${item.status}`" />
											{{ item.text }}
										</v-list-item-title>
										<v-list-item-subtitle>{{ item.value }} &mdash; {{ item.modelName || $t('yealink-config-unknown-model') }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
								<template #selection="{ item }">
									<span class="d-flex align-center">
										<span class="device-status-pill mr-2" :class="`device-status-${item.status}`" />
										{{ item.text }}
									</span>
								</template>
								<template #append>
									<v-icon small class="mt-1 mac-toggle-icon" @click.stop="manualMacInput = true">mdi-form-textbox</v-icon>
								</template>
							</v-select>
							<v-text-field
								v-else
								v-model="macAddress"
								:label="$t('yealink-config-mac-address')"
								outlined
								dense
								clearable
								prepend-inner-icon="mdi-ethernet"
								hint=" "
								persistent-hint
								:rules="macRules"
								@input="formatMacAddress"
							>
								<template #message>
									{{ $t('yealink-config-mac-hint') }}
									<a class="mac-hint-link" @click.prevent="openYealinkPanel">
										{{ $t('yealink-config-add-devices') }}
									</a>
								</template>
								<template #append>
									<v-icon small class="mt-1 mac-toggle-icon" @click.stop="manualMacInput = false; onMacAddressChange(macAddress)">mdi-format-list-bulleted</v-icon>
								</template>
							</v-text-field>
						</v-col>

						<!-- Phone Model Selection -->
						<v-col cols="12" md="6">
							<v-select
								v-model="selectedModel"
								:items="phoneModels"
								:label="$t('yealink-config-phone-model') + ' *'"
								item-text="name"
								item-value="id"
								return-object
								outlined
								dense
								:rules="[v => !!v || $t('api-required')]"
								prepend-inner-icon="mdi-deskphone"
							/>
						</v-col>
					</v-row>
				</div>

				<!-- Account Section -->
				<div class="config-section mb-4">
					<div class="section-title mb-4 d-flex align-center">
						<v-icon small class="mr-2">mdi-account-voice</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-account') }}</span>
					</div>
					<v-row dense>
						<!-- SIP User Selection -->
						<v-col cols="12" md="6">
							<v-select
								v-model="selectedSipUser"
								:items="sipUsers"
								:label="$t('yealink-config-select-sip-user') + ' *'"
								:item-text="u => `${u.username} — ${u.displayName}`"
								item-value="username"
								return-object
								outlined
								dense
								:rules="[v => !!v || $t('api-required')]"
								prepend-inner-icon="mdi-account-voice"
							>
								<template #item="{ item }">
									<v-list-item-content>
										<v-list-item-title class="font-weight-bold">
											{{ item.username }}
										</v-list-item-title>
										<v-list-item-subtitle>{{ item.displayName }}</v-list-item-subtitle>
									</v-list-item-content>
								</template>
								<template #selection="{ item }">
									<span class="font-weight-bold mr-1">{{ item.username }}</span>
									<span class="text--secondary">— {{ item.displayName }}</span>
								</template>
							</v-select>
						</v-col>

						<!-- Display Name Override -->
						<v-col cols="12" md="6">
							<v-text-field
								v-model="displayName"
								:label="$t('yealink-config-display-name') + ' *'"
								outlined
								dense
								:rules="[v => !!v || $t('api-required')]"
								prepend-inner-icon="mdi-label"
								:placeholder="selectedSipUser ? selectedSipUser.displayName : ''"
							/>
						</v-col>
						<!-- Ring Type -->
						<v-col v-if="selectedModel && selectedModel.id !== 'w'" cols="12" md="6">
							<v-select
								v-model="ringType"
								:items="ringTypeOptions"
								:label="$t('yealink-config-ring-type')"
								outlined
								dense
								hide-details
								prepend-inner-icon="mdi-bell-ring-outline"
							/>
						</v-col>
						<!-- Exclude from DSS -->
						<v-col v-if="selectedModel && selectedModel.dssKeyCount > 0" cols="12" md="6" class="d-flex align-center justify-center">
							<v-checkbox
								v-model="excludeFromDss"
								:label="$t('yealink-config-exclude-from-dss')"
								hide-details
								dense
								class="mt-0 pt-0"
								@change="generateDefaultDssKeys"
							/>
						</v-col>
					</v-row>

					<!-- No Password Warning -->
					<v-alert
						v-if="selectedSipUser && !selectedSipUser.password && !showPasswordInput && !passwordManuallySet"
						type="error"
						outlined
						dense
						class="mt-2 mb-0 alert-centered-icon"
					>
						<div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-sm-space-between">
							<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-no-password-warning-account', { name: selectedSipUser.displayName || selectedSipUser.username }) }}</span>
							<v-btn
								small
								text
								color="primary"
								class="mt-2 mt-sm-0 ml-sm-3"
								@click="showPasswordInput = true"
							>
								<v-icon small left>mdi-key</v-icon>
								{{ $t('yealink-config-set-password') }}
							</v-btn>
						</div>
					</v-alert>

					<!-- Password Input -->
					<v-alert
						v-if="selectedSipUser && showPasswordInput && !passwordManuallySet"
						type="info"
						outlined
						dense
						:icon="false"
						class="mt-2 mb-0"
					>
						<div class="d-flex align-center">
							<v-icon color="info" class="mr-3">mdi-key</v-icon>
							<v-text-field
								v-model="manualPassword"
								:label="`${$t('yealink-config-password')} (${selectedSipUser.displayName || selectedSipUser.username})`"
								type="password"
								dense
								hide-details
								outlined
								class="mr-3"
								style="max-width: 300px;"
							/>
							<v-btn small color="primary" :disabled="!manualPassword" @click="applyManualPassword">
								{{ $t('apply') }}
							</v-btn>
							<v-btn small text @click="showPasswordInput = false; manualPassword = ''">
								{{ $t('cancel') }}
							</v-btn>
						</div>
					</v-alert>

					<!-- Manually Set Password Warning -->
					<v-alert
						v-if="selectedSipUser && passwordManuallySet"
						type="warning"
						outlined
						dense
						icon="mdi-key-alert"
						class="mt-2 mb-0"
					>
						<div class="d-flex align-center justify-space-between">
							<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-password-manually-set-account', { name: selectedSipUser.displayName || selectedSipUser.username }) }}</span>
							<v-btn small text color="grey" @click="clearManualPassword">
								<v-icon small left>mdi-close</v-icon>
								{{ $t('clear') }}
							</v-btn>
						</div>
					</v-alert>
				</div>

				<!-- Additional Accounts -->
				<div class="config-section mb-4">
					<div class="section-title mb-4 d-flex align-center">
						<v-icon small class="mr-2">mdi-account-multiple-plus</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-additional-accounts') }}</span>
						<span v-if="selectedModel" class="text--secondary ml-2">({{ additionalAccounts.length }}/{{ maxAdditionalAccounts }})</span>
					</div>

					<div v-for="(acc, idx) in additionalAccounts" :key="idx" class="additional-account-row mb-3">
						<v-row dense>
							<v-col cols="12" md="6">
								<v-select
									v-model="acc.sipUser"
									:items="getAvailableSipUsersFor(idx)"
									:label="`${$t('yealink-config-select-sip-user')} (${idx + 2})`"
									item-text="displayName"
									item-value="username"
									return-object
									outlined
									dense
									prepend-inner-icon="mdi-account-voice"
									:hint="acc.sipUser ? `${$t('yealink-config-extension')}: ${acc.sipUser.username}` : ''"
									persistent-hint
									@change="onAdditionalAccountChange(idx)"
								>
									<template #item="{ item }">
										<v-list-item-content>
											<v-list-item-title>{{ item.displayName }}</v-list-item-title>
											<v-list-item-subtitle>
												{{ $t('yealink-config-extension') }}: {{ item.username }}
											</v-list-item-subtitle>
										</v-list-item-content>
									</template>
								</v-select>
							</v-col>
							<v-col cols="12" md="6">
								<v-text-field
									v-model="acc.displayName"
									:label="`${$t('yealink-config-display-name')} (${idx + 2})`"
									outlined
									dense
									prepend-inner-icon="mdi-label"
									:placeholder="acc.sipUser ? acc.sipUser.displayName : ''"
								/>
							</v-col>
							<!-- Ring Type, Exclude DSS & Delete -->
							<v-col cols="12">
								<v-row dense no-gutters>
									<v-col v-if="selectedModel && selectedModel.id !== 'w'" cols="12" md="6">
										<v-select
											v-model="acc.ringType"
											:items="ringTypeOptions"
											:label="`${$t('yealink-config-ring-type')} (${idx + 2})`"
											outlined
											dense
											hide-details
											prepend-inner-icon="mdi-bell-ring-outline"
										/>
									</v-col>
									<v-col cols="12" md="6" class="d-flex align-center justify-center pl-md-3 mt-2 mt-md-0">
										<v-checkbox
											v-if="selectedModel && selectedModel.dssKeyCount > 0"
											v-model="acc.excludeFromDss"
											:label="$t('yealink-config-exclude-from-dss')"
											hide-details
											dense
											class="mt-0 pt-0 mr-2"
											@change="generateDefaultDssKeys"
										/>
										<v-btn
											small
											text
											color="error"
											@click="removeAdditionalAccount(idx)"
										>
											<v-icon small left>mdi-delete</v-icon>
											{{ $t('yealink-config-delete-account') }}
										</v-btn>
									</v-col>
								</v-row>
							</v-col>
						</v-row>

						<!-- No Password Warning for additional account -->
						<v-alert
							v-if="acc.sipUser && !acc.sipUser.password && !acc.showPasswordInput && !acc.passwordManuallySet"
							type="error"
							outlined
							dense
							class="mt-2 mb-0 alert-centered-icon"
						>
							<div class="d-flex flex-column flex-sm-row align-start align-sm-center justify-sm-space-between">
								<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-no-password-warning-account', { name: acc.sipUser.displayName || acc.sipUser.username }) }}</span>
								<v-btn
									small
									text
									color="primary"
									class="mt-2 mt-sm-0 ml-sm-3"
									@click="$set(acc, 'showPasswordInput', true)"
								>
									<v-icon small left>mdi-key</v-icon>
									{{ $t('yealink-config-set-password') }}
								</v-btn>
							</div>
						</v-alert>

						<!-- Password Input for additional account -->
						<v-alert
							v-if="acc.sipUser && acc.showPasswordInput && !acc.passwordManuallySet"
							type="info"
							outlined
							dense
							:icon="false"
							class="mt-2 mb-0"
						>
							<div class="d-flex align-center">
								<v-icon color="info" class="mr-3">mdi-key</v-icon>
								<v-text-field
									v-model="acc.manualPassword"
									:label="`${$t('yealink-config-password')} (${acc.sipUser.displayName || acc.sipUser.username})`"
									type="password"
									dense
									hide-details
									outlined
									class="mr-3"
									style="max-width: 300px;"
								/>
								<v-btn small color="primary" :disabled="!acc.manualPassword" @click="applyAdditionalPassword(idx)">
									{{ $t('apply') }}
								</v-btn>
								<v-btn small text @click="$set(acc, 'showPasswordInput', false); $set(acc, 'manualPassword', '')">
									{{ $t('cancel') }}
								</v-btn>
							</div>
						</v-alert>

						<!-- Manually Set Password Warning for additional account -->
						<v-alert
							v-if="acc.sipUser && acc.passwordManuallySet"
							type="warning"
							outlined
							dense
							icon="mdi-key-alert"
							class="mt-2 mb-0"
						>
							<div class="d-flex align-center justify-space-between">
								<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-password-manually-set-account', { name: acc.sipUser.displayName || acc.sipUser.username }) }}</span>
								<v-btn small text color="grey" @click="clearAdditionalPassword(idx)">
									<v-icon small left>mdi-close</v-icon>
									{{ $t('clear') }}
								</v-btn>
							</div>
						</v-alert>
					</div>

					<v-btn
						x-small
						text
						color="primary"
						class="mt-1"
						:disabled="!selectedSipUser || !selectedModel || additionalAccounts.length >= maxAdditionalAccounts"
						@click="addAdditionalAccount"
					>
						<v-icon x-small left>mdi-plus</v-icon>
						{{ $t('yealink-config-add-account') }}
					</v-btn>
				</div>

				<!-- Handset Association -->
				<div v-if="selectedSipUser && selectedModel && isWirelessModel" class="advanced-settings mb-4">
					<div class="section-title mb-2 d-flex align-center">
						<v-icon small class="mr-2">mdi-cellphone-wireless</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-handset-association') }}</span>
						<span class="text--secondary ml-2">({{ handsets.length }}/{{ maxHandsets }})</span>
					</div>

					<div class="handset-list">
						<div class="d-flex align-center text--secondary text-caption mb-1" style="padding-left: 4px;">
							<span style="width: 24px; flex-shrink: 0;">#</span>
							<span class="flex-grow-1">{{ $t('yealink-config-handset-account') }}</span>
							<span style="width: 56px; text-align: center; flex-shrink: 0;">{{ $t('actions') }}</span>
						</div>
						<div v-for="(hs, idx) in handsets" :key="idx" class="d-flex align-center mb-1" style="padding-left: 4px;">
							<span class="text-body-2 text--secondary font-weight-medium" style="width: 24px; flex-shrink: 0;">{{ hs.index }}</span>
							<v-select
								v-model.number="hs.account"
								:items="handsetAccountOptions"
								item-text="text"
								item-value="value"
								dense
								hide-details
								outlined
								class="dss-input flex-grow-1"
							/>
							<div style="width: 56px; flex-shrink: 0; text-align: center;">
								<v-btn
									icon
									x-small
									color="error"
									:disabled="idx === 0"
									@click="removeHandset(idx)"
								>
									<v-icon x-small>mdi-delete</v-icon>
								</v-btn>
							</div>
						</div>
					</div>

					<v-btn
						x-small
						text
						color="primary"
						class="mt-2"
						:disabled="handsets.length >= maxHandsets"
						@click="addHandset"
					>
						<v-icon x-small left>mdi-plus</v-icon>
						{{ $t('yealink-config-add-handset') }}
					</v-btn>
				</div>

				<!-- DSS Keys Preview -->
				<div v-if="selectedSipUser && selectedModel" class="dss-preview mb-4">
					<div class="section-title mb-2 d-flex align-center">
						<v-icon small class="mr-2">mdi-dialpad</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-dss-keys') }}</span>
						<span v-if="dssKeysEnabled" class="text--secondary ml-2">({{ dssKeys.length }}/{{ maxDssKeys }})</span>
						<v-spacer />
						<v-switch
							v-model="dssKeysEnabled"
							hide-details
							dense
							inset
							:disabled="selectedModel && selectedModel.dssKeyCount === 0"
							class="mt-0 pt-0 mr-1"
						/>
						<v-btn
							v-if="dssKeysEnabled"
							x-small
							text
							color="grey"
							@click="resetDssKeys"
						>
							<v-icon x-small left>mdi-refresh</v-icon>
							{{ $t('reset') }}
						</v-btn>
					</div>

					<!-- DSS Keys Disabled Message -->
					<div v-if="!dssKeysEnabled" class="dss-disabled-message">
						<v-icon color="grey" size="40">mdi-dialpad</v-icon>
						<span class="text--secondary text-body-2 mt-2">{{ selectedModel && selectedModel.dssKeyCount === 0 ? $t('yealink-config-dss-not-supported') : $t('yealink-config-dss-disabled') }}</span>
					</div>

					<template v-else>
						<div class="d-flex flex-wrap align-center justify-space-between mb-3" style="gap: 16px;">
							<v-checkbox
								v-model="intercomEnabled"
								:label="$t('yealink-config-enable-intercom')"
								hide-details
								dense
								class="mt-0"
								@change="onIntercomToggle"
							/>
							<v-select
								v-model="dssKeyLength"
								:items="dssKeyLengthOptions"
								:label="$t('yealink-config-dss-key-length')"
								outlined
								dense
								hide-details
								style="max-width: 220px; min-width: 180px;"
							/>
						</div>

						<div class="dss-table-scroll">
							<v-simple-table dense class="dss-table">
								<template #default>
									<thead>
										<tr>
											<th style="width: 40px;" />
											<th class="text-left" style="width: 50px;">
												#
											</th>
											<th class="text-left" style="min-width: 110px;">
												{{ $t('yealink-config-type') }}
											</th>
											<th class="text-left" style="min-width: 120px;">
												{{ $t('yealink-config-label') }}
											</th>
											<th class="text-left" style="min-width: 120px;">
												{{ $t('yealink-config-value') }}
											</th>
											<th class="text-left" style="width: 70px;">
												{{ $t('yealink-config-line') }}
											</th>
											<th class="text-center" style="width: 50px;">
												{{ $t('actions') }}
											</th>
										</tr>
									</thead>
									<draggable
										v-model="dssKeys"
										tag="tbody"
										handle=".drag-handle"
										:filter="'.no-drag'"
										:prevent-on-filter="false"
										@end="onDragEnd"
									>
										<tr v-for="(key, idx) in dssKeys" :key="idx" class="dss-row" :class="{ 'no-drag': isLignesKey(key) }">
											<td class="icon-cell" :class="{ 'drag-handle': !isLignesKey(key) }">
												<v-icon small class="drag-icon">{{ isLignesKey(key) ? 'mdi-lock' : 'mdi-drag' }}</v-icon>
											</td>
											<td>{{ key.index }}</td>
											<td>
												<v-select
													v-model="key.type"
													:items="dssKeyTypes"
													item-text="text"
													item-value="value"
													dense
													hide-details
													outlined
													class="dss-select"
													@change="onDssTypeChange(key)"
												/>
											</td>
											<td>
												<v-text-field
													v-model="key.label"
													dense
													hide-details
													outlined
													class="dss-input"
													:readonly="key.type === 0 || key.type === 24"
													:disabled="key.type === 0 || key.type === 24"
												/>
											</td>
											<td>
												<v-text-field
													v-model="key.value"
													dense
													hide-details
													outlined
													class="dss-input"
													:readonly="key.type === -1 || key.type === 0 || key.type === 24"
													:disabled="key.type === -1 || key.type === 0 || key.type === 24"
												/>
											</td>
											<td>
												<v-text-field
													v-model.number="key.line"
													type="number"
													dense
													hide-details
													outlined
													class="dss-input"
													:readonly="key.type !== -1"
													:disabled="key.type !== -1"
												/>
											</td>
											<td class="text-center">
												<v-btn
													icon
													x-small
													color="error"
													@click="removeDssKey(idx)"
												>
													<v-icon x-small>mdi-delete</v-icon>
												</v-btn>
											</td>
										</tr>
									</draggable>
								</template>
							</v-simple-table>
						</div>

						<v-btn
							x-small
							text
							color="primary"
							class="mt-2"
							:disabled="dssKeys.length >= maxDssKeys"
							@click="addDssKey"
						>
							<v-icon x-small left>mdi-plus</v-icon>
							{{ $t('yealink-config-add-dss-key') }}
						</v-btn>
					</template>
				</div>

				<!-- Advanced Settings -->
				<div v-if="selectedSipUser && selectedModel" class="advanced-settings mb-4">
					<div class="section-title mb-4 d-flex align-center">
						<v-icon small class="mr-2">mdi-cog-outline</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-advanced-settings') }}</span>
					</div>
					<v-row dense>
						<!-- SIP Port -->
						<v-col cols="6" md="3">
							<v-text-field
								v-model.number="sipPort"
								:label="$t('yealink-config-sip-port')"
								type="number"
								outlined
								dense
								:hint="$t('yealink-config-sip-port-hint')"
								persistent-hint
								:rules="portRules"
							/>
						</v-col>

						<!-- RTP Min Port -->
						<v-col cols="6" md="3">
							<v-text-field
								v-model.number="rtpMinPort"
								:label="$t('yealink-config-rtp-min')"
								type="number"
								outlined
								dense
								:rules="portRules"
							>
								<template #append>
									<div class="d-flex flex-column">
										<v-icon small class="rtp-stepper" @click="rtpMinPort += 2000">mdi-chevron-up</v-icon>
										<v-icon small class="rtp-stepper" @click="rtpMinPort -= 2000">mdi-chevron-down</v-icon>
									</div>
								</template>
							</v-text-field>
						</v-col>

						<!-- RTP Max Port -->
						<v-col cols="6" md="3">
							<v-text-field
								:value="rtpMaxPort"
								:label="$t('yealink-config-rtp-max')"
								type="number"
								outlined
								dense
								readonly
								disabled
								:error="rtpMaxPort > 65535"
								:error-messages="rtpMaxPort > 65535 ? [$t('yealink-config-port-max-error')] : []"
								:class="{ 'rtp-max-error': rtpMaxPort > 65535 }"
							/>
						</v-col>

						<!-- NAT Traversal -->
						<v-col cols="6" md="3">
							<v-select
								v-model="natTraversal"
								:items="natTraversalOptions"
								:label="$t('yealink-config-nat-traversal')"
								item-text="text"
								item-value="value"
								outlined
								dense
								@blur="onNetworkSettingBlur"
							/>
						</v-col>

						<!-- Transport Type -->
						<v-col cols="6" md="3">
							<v-select
								v-model="transportType"
								:items="transportTypeOptions"
								:label="$t('yealink-config-transport-type')"
								item-text="text"
								item-value="value"
								outlined
								dense
								@blur="onNetworkSettingBlur"
							/>
						</v-col>

						<!-- Timezone -->
						<v-col cols="6" md="5">
							<v-select
								v-model="timezone"
								:items="timezoneOptions"
								:label="$t('yealink-config-timezone')"
								item-text="text"
								item-value="value"
								outlined
								dense
							/>
						</v-col>

						<!-- GUI Language -->
						<v-col cols="6" md="4">
							<v-select
								v-model="guiLanguage"
								:items="guiLanguageOptions"
								:label="$t('yealink-config-language')"
								item-text="text"
								item-value="value"
								outlined
								dense
							/>
						</v-col>

						<!-- WAN VLAN ID -->
						<v-col cols="6" md="3">
							<v-text-field
								v-model="wanVlanId"
								:label="$t('yealink-config-wan-vlan-id')"
								outlined
								dense
								:hint="$t('yealink-config-wan-vlan-hint')"
								persistent-hint
								:rules="vlanRules"
								placeholder="100"
								@blur="onNetworkSettingBlur"
							/>
						</v-col>
					</v-row>

					<!-- Port Mismatch Warnings -->
					<v-alert
						v-if="selectedSipUser && sipPort !== expectedSipPort"
						type="warning"
						outlined
						dense
						class="mt-2 mb-0 alert-centered-icon"
					>
						<div class="d-flex align-center justify-space-between">
							<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-sip-port-mismatch', { expected: expectedSipPort, actual: sipPort }) }}</span>
							<v-btn small text color="warning" @click="sipPort = expectedSipPort">
								<v-icon small left>mdi-refresh</v-icon>
								{{ $t('reset') }}
							</v-btn>
						</div>
					</v-alert>

					<v-alert
						v-if="selectedSipUser && rtpMinPort !== expectedRtpMinPort"
						type="warning"
						outlined
						dense
						class="mt-2 mb-0 alert-centered-icon"
					>
						<div class="d-flex align-center justify-space-between">
							<span class="text-body-2 font-weight-bold">{{ $t('yealink-config-rtp-port-mismatch', { expected: expectedRtpMinPort, actual: rtpMinPort }) }}</span>
							<v-btn small text color="warning" @click="rtpMinPort = expectedRtpMinPort">
								<v-icon small left>mdi-refresh</v-icon>
								{{ $t('reset') }}
							</v-btn>
						</div>
					</v-alert>
				</div>

				<!-- Config Preview -->
				<div v-if="selectedSipUser && selectedModel" class="config-preview mb-4">
					<div class="section-title mb-2 d-flex align-center">
						<v-icon small class="mr-2">mdi-file-document-outline</v-icon>
						<span class="font-weight-medium">{{ $t('yealink-config-preview') }}</span>
						<v-spacer />
						<template v-if="selectedDeviceId">
							<span v-if="currentConfigLoading" class="d-flex align-center">
								<v-progress-circular
									indeterminate
									size="16"
									width="2"
									color="cyan"
									class="mr-2"
								/>
								<span class="text-caption text--secondary">{{ $t('yealink-config-diff-loading') }}</span>
							</span>
							<v-chip v-if="currentConfig && !currentConfigLoading" x-small color="success" outlined>
								<v-icon x-small left>mdi-check-circle</v-icon>
								{{ $t('yealink-config-diff-loaded') }}
							</v-chip>
						</template>
					</div>

					<!-- Export Error -->
					<v-alert
						v-if="currentConfigError"
						type="error"
						outlined
						dense
						class="mb-2"
					>
						<div class="d-flex align-center justify-space-between">
							<span class="text-body-2">{{ currentConfigError }}</span>
							<v-btn
								small
								text
								color="error"
								:loading="currentConfigLoading"
								@click="fetchCurrentConfig"
							>
								<v-icon small left>mdi-refresh</v-icon>
								{{ $t('yealink-config-diff-retry') }}
							</v-btn>
						</div>
					</v-alert>

					<!-- Diff Summary -->
					<div v-if="currentConfig && configDiff" class="diff-summary mb-2 d-flex align-center flex-wrap">
						<span class="diff-badge diff-badge-added">
							<v-icon x-small color="#4caf50" class="mr-1">mdi-plus-circle</v-icon>
							{{ configDiff.added }} {{ $t('yealink-config-diff-added') }}
						</span>
						<span class="diff-badge diff-badge-modified">
							<v-icon x-small color="#ff9800" class="mr-1">mdi-pencil-circle</v-icon>
							{{ configDiff.modified }} {{ $t('yealink-config-diff-modified') }}
						</span>
						<span class="diff-badge diff-badge-unchanged">
							<v-icon x-small color="grey" class="mr-1">mdi-check-circle-outline</v-icon>
							{{ configDiff.unchanged }} {{ $t('yealink-config-diff-unchanged') }}
						</span>
						<span class="diff-badge diff-badge-removed">
							<v-icon x-small color="#f44336" class="mr-1">mdi-minus-circle</v-icon>
							{{ configDiff.removed }} {{ $t('yealink-config-diff-not-in-config') }}
						</span>
					</div>

					<!-- eslint-disable-next-line vue/no-v-html -->
					<pre class="config-preview-box" v-html="configPreviewHighlighted" />
				</div>

				<!-- Actions -->
				<div class="config-actions d-flex flex-wrap justify-end gap-2">
					<v-btn
						outlined
						color="grey"
						@click="resetForm"
					>
						<v-icon small left>mdi-refresh</v-icon>
						{{ $t('reset') }}
					</v-btn>

					<v-btn
						color="primary"
						:disabled="!canGenerate"
						@click="downloadConfig"
					>
						<v-icon small left>mdi-download</v-icon>
						{{ $t('yealink-config-download') }}
					</v-btn>

					<v-tooltip top :disabled="!!selectedDeviceId">
						<template #activator="{ on, attrs }">
							<span v-bind="attrs" v-on="on">
								<v-btn
									color="cyan"
									:disabled="!canGenerate || !selectedDeviceId"
									:loading="pushing"
									class="white--text"
									@click="pushToYmcs"
								>
									<v-icon small left>mdi-cloud-upload</v-icon>
									{{ $t('yealink-config-push-ymcs') }}
								</v-btn>
							</span>
						</template>
						<span>{{ $t('yealink-config-push-ymcs-no-device') }}</span>
					</v-tooltip>
				</div>
			</div>

			<!-- Network Settings Diff Dialog -->
			<v-dialog
				v-model="networkDiffDialog"
				max-width="720"
				persistent
				:fullscreen="$vuetify.breakpoint.xsOnly"
				content-class="yealink-fullwidth-dialog"
			>
				<v-card class="yealink-dialog-card">
					<v-card-title class="yealink-dialog-title blue-grey lighten-5">
						<v-icon color="blue-grey darken-2" class="mr-2">mdi-sync</v-icon>
						<span class="text-subtitle-1 font-weight-bold blue-grey--text text--darken-4">
							{{ $t('yealink-config-network-diff-title') }}
						</span>
					</v-card-title>

					<v-divider />

					<v-card-text class="yealink-dialog-text">
						<p class="yealink-dialog-lead mb-2">
							{{ $t('yealink-config-network-diff-lead') }}
						</p>
						<p class="yealink-dialog-body mb-4">
							{{ $t('yealink-config-network-diff-explanation') }}
						</p>

						<p class="yealink-dialog-scope-note mb-2">
							<v-icon x-small color="blue-grey darken-1" class="mr-1">mdi-information-outline</v-icon>
							{{ $t('yealink-config-network-diff-scope-note') }}
						</p>
						<v-simple-table dense class="network-diff-table mb-5">
							<template #default>
								<thead>
									<tr>
										<th class="text-left">
											{{ $t('yealink-config-network-diff-variable') }}
										</th>
										<th class="text-left">
											{{ $t('yealink-config-network-diff-previous') }}
										</th>
										<th class="text-left">
											{{ $t('yealink-config-network-diff-applied') }}
										</th>
									</tr>
								</thead>
								<tbody>
									<tr v-for="d in networkDiffs" :key="d.key">
										<td class="font-weight-medium yealink-diff-key">
											{{ d.key }}
										</td>
										<td class="yealink-diff-previous">
											{{ d.previousLabel }}
										</td>
										<td class="yealink-diff-applied">
											<v-icon x-small color="success" class="mr-1">mdi-arrow-right-bold</v-icon>
											{{ d.appliedLabel }}
										</td>
									</tr>
								</tbody>
							</template>
						</v-simple-table>

						<h4 class="yealink-dialog-section-title mb-2">
							<v-icon small color="blue-grey darken-2" class="mr-1">mdi-magnify</v-icon>
							{{ $t('yealink-config-network-diff-review-title') }}
						</h4>
						<p class="yealink-dialog-body mb-2">
							{{ $t('yealink-config-network-diff-review-intro') }}
						</p>
						<ul class="yealink-dialog-list mb-3">
							<li>{{ $t('yealink-config-network-diff-risk-1') }}</li>
							<li>{{ $t('yealink-config-network-diff-risk-2') }}</li>
							<li>{{ $t('yealink-config-network-diff-risk-3') }}</li>
						</ul>
						<p class="yealink-dialog-closing mb-4">
							{{ $t('yealink-config-network-diff-closing') }}
						</p>

						<v-alert
							dense
							outlined
							type="error"
							class="mb-0 yealink-dialog-alert"
							icon="mdi-alert-octagon"
						>
							<span class="text-body-2 font-weight-medium">
								{{ $t('yealink-config-network-diff-warning') }}
							</span>
						</v-alert>
					</v-card-text>

					<v-divider />

					<v-card-actions class="yealink-dialog-actions">
						<v-btn
							:color="revertConfirmArmed ? 'error darken-2' : 'error'"
							:depressed="!$vuetify.breakpoint.xsOnly || revertConfirmArmed"
							:outlined="$vuetify.breakpoint.xsOnly && !revertConfirmArmed"
							:large="$vuetify.breakpoint.xsOnly"
							:block="$vuetify.breakpoint.xsOnly"
							class="yealink-dialog-btn yealink-dialog-btn-danger"
							@click="onRevertClick"
						>
							<v-icon small left>{{ revertConfirmArmed ? 'mdi-alert' : 'mdi-undo-variant' }}</v-icon>
							{{ revertConfirmArmed ? $t('yealink-config-network-diff-revert-confirm') : $t('yealink-config-network-diff-revert') }}
						</v-btn>
						<v-spacer v-if="!$vuetify.breakpoint.xsOnly" />
						<v-btn
							color="primary"
							depressed
							:large="$vuetify.breakpoint.xsOnly"
							:block="$vuetify.breakpoint.xsOnly"
							class="yealink-dialog-btn yealink-dialog-btn-safe"
							@click="acceptNetworkSettings"
						>
							<v-icon small left>mdi-check</v-icon>
							{{ $t('yealink-config-network-diff-accept') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>

			<!-- Deploy Gate Dialog (current config diff not yet loaded) -->
			<v-dialog
				v-model="deployGateDialog"
				max-width="600"
				persistent
				:fullscreen="$vuetify.breakpoint.xsOnly"
				content-class="yealink-fullwidth-dialog"
			>
				<v-card class="yealink-dialog-card">
					<v-card-title class="yealink-dialog-title red lighten-5">
						<v-icon color="error" class="mr-2">mdi-alert-octagon-outline</v-icon>
						<span class="text-subtitle-1 font-weight-bold red--text text--darken-4">
							{{ $t('yealink-config-deploy-gate-title') }}
						</span>
						<v-spacer />
						<v-btn icon small @click="cancelDeployGate">
							<v-icon>mdi-close</v-icon>
						</v-btn>
					</v-card-title>

					<v-divider />

					<v-card-text class="yealink-dialog-text">
						<p class="yealink-dialog-lead mb-2">
							{{ $t('yealink-config-deploy-gate-lead') }}
						</p>
						<p class="yealink-dialog-body mb-4">
							{{ $t('yealink-config-deploy-gate-explanation') }}
						</p>

						<p class="yealink-dialog-body mb-2">
							{{ $t('yealink-config-deploy-gate-benefits-intro') }}
						</p>
						<ul class="yealink-dialog-list mb-4">
							<li>{{ $t('yealink-config-deploy-gate-benefit-1') }}</li>
							<li>{{ $t('yealink-config-deploy-gate-benefit-2') }}</li>
							<li>{{ $t('yealink-config-deploy-gate-benefit-3') }}</li>
						</ul>

						<v-alert
							dense
							outlined
							type="error"
							class="mb-0 yealink-dialog-alert"
							icon="mdi-alert-octagon"
						>
							<span class="text-body-2 font-weight-medium">
								{{ $t('yealink-config-deploy-gate-warning') }}
							</span>
						</v-alert>
					</v-card-text>

					<v-divider />

					<v-card-actions class="yealink-dialog-actions">
						<v-btn
							:color="deployConfirmArmed ? 'error darken-2' : 'error'"
							:depressed="!$vuetify.breakpoint.xsOnly || deployConfirmArmed"
							:outlined="$vuetify.breakpoint.xsOnly && !deployConfirmArmed"
							:large="$vuetify.breakpoint.xsOnly"
							:block="$vuetify.breakpoint.xsOnly"
							class="yealink-dialog-btn yealink-dialog-btn-danger"
							@click="onDeployGateClick"
						>
							<v-icon small left>{{ deployConfirmArmed ? 'mdi-alert' : 'mdi-rocket-launch' }}</v-icon>
							{{ deployConfirmArmed ? $t('yealink-config-deploy-gate-confirm-again') : $t('yealink-config-deploy-gate-confirm') }}
						</v-btn>
						<v-spacer v-if="!$vuetify.breakpoint.xsOnly" />
						<v-btn
							color="primary"
							depressed
							:large="$vuetify.breakpoint.xsOnly"
							:block="$vuetify.breakpoint.xsOnly"
							class="yealink-dialog-btn yealink-dialog-btn-safe"
							@click="cancelDeployGate"
						>
							<v-icon small left>mdi-close</v-icon>
							{{ $t('cancel') }}
						</v-btn>
					</v-card-actions>
				</v-card>
			</v-dialog>
		</v-card-text>
	</v-card>
</template>

<script>
import draggable from "vuedraggable";

export default {
	name: "YealinkConfigPanel",

	components: {
		draggable
	},

	props: {
		embedded: {
			type: Boolean,
			default: false
		}
	},

	data() {
		return {
			loading: false,
			pushing: false,
			error: null,
			configData: null,
			sipUsers: [],
			devices: [],
			selectedSipUser: null,
			selectedModel: null,
			macAddress: "",
			manualMacInput: false,
			displayName: "",
			sipPort: 5080,
			rtpMinPort: 11780,
			timezone: -5,
			natTraversal: 1,
			transportType: 1,
			guiLanguage: "French_CA",
			ringType: "Common",
			wanVlanId: "",
			excludeFromDss: true,
			showPasswordInput: false,
			manualPassword: "",
			passwordManuallySet: false,
			dssKeysEnabled: true,
			dssKeyLength: "0",

			currentConfig: null,
			currentConfigLoading: false,
			currentConfigError: null,
			currentConfigDiagnosisId: null,
			currentConfigPollInterval: null,
			currentConfigRequestId: 0,

			networkDiffDialog: false,
			networkDiffs: [],
			networkDiffOriginals: null,
			revertConfirmArmed: false,
			revertConfirmTimer: null,

			deployGateDialog: false,
			deployWithoutDiffConfirmed: false,
			deployConfirmArmed: false,
			deployConfirmTimer: null,

			phoneModels: [
				{ "id": "t19p-e2", "name": "SIP-T19(P) E2", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 1 },
				{ "id": "t23g", "name": "SIP-T23G", "dssKeyCount": 3, "lignesPosition": 1, "maxAccounts": 3 },
				{ "id": "t23p", "name": "SIP-T23P", "dssKeyCount": 3, "lignesPosition": 1, "maxAccounts": 3 },
				{ "id": "t27g", "name": "SIP-T27G", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 6 },
				{ "id": "t27p", "name": "SIP-T27P", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 6 },
				{ "id": "t29g", "name": "SIP-T29G", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t30", "name": "SIP-T30", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 1 },
				{ "id": "t30p", "name": "SIP-T30P", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 1 },
				{ "id": "t31", "name": "SIP-T31", "dssKeyCount": 2, "lignesPosition": 1, "maxAccounts": 2 },
				{ "id": "t31g", "name": "SIP-T31G", "dssKeyCount": 2, "lignesPosition": 1, "maxAccounts": 2 },
				{ "id": "t31p", "name": "SIP-T31P", "dssKeyCount": 2, "lignesPosition": 1, "maxAccounts": 2 },
				{ "id": "t31w", "name": "SIP-T31W", "dssKeyCount": 2, "lignesPosition": 1, "maxAccounts": 2 },
				{ "id": "t33g", "name": "SIP-T33G", "dssKeyCount": 12, "lignesPosition": 1, "maxAccounts": 4 },
				{ "id": "t33p", "name": "SIP-T33P", "dssKeyCount": 12, "lignesPosition": 1, "maxAccounts": 4 },
				{ "id": "t34w", "name": "SIP-T34W", "dssKeyCount": 12, "lignesPosition": 1, "maxAccounts": 4 },
				{ "id": "t40g", "name": "SIP-T40G", "dssKeyCount": 3, "lignesPosition": 1, "maxAccounts": 3 },
				{ "id": "t40p", "name": "SIP-T40P", "dssKeyCount": 3, "lignesPosition": 1, "maxAccounts": 3 },
				{ "id": "t41p", "name": "SIP-T41P", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 6 },
				{ "id": "t41s", "name": "SIP-T41S", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 6 },
				{ "id": "t41u", "name": "SIP-T41U", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 6 },
				{ "id": "t42g", "name": "SIP-T42G", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 12 },
				{ "id": "t42s", "name": "SIP-T42S", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 12 },
				{ "id": "t42u", "name": "SIP-T42U", "dssKeyCount": 15, "lignesPosition": 4, "maxAccounts": 12 },
				{ "id": "t43u", "name": "SIP-T43U", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t44u", "name": "SIP-T44U", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t44w", "name": "SIP-T44W", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t46g", "name": "SIP-T46G", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t46s", "name": "SIP-T46S", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t46u", "name": "SIP-T46U", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t48g", "name": "SIP-T48G", "dssKeyCount": 29, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t48s", "name": "SIP-T48S", "dssKeyCount": 29, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t48u", "name": "SIP-T48U", "dssKeyCount": 29, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t52s", "name": "SIP-T52S", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t53", "name": "SIP-T53", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t53c", "name": "SIP-T53C", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t53w", "name": "SIP-T53W", "dssKeyCount": 21, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t54s", "name": "SIP-T54S", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t54w", "name": "SIP-T54W", "dssKeyCount": 27, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t57w", "name": "SIP-T57W", "dssKeyCount": 29, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t58", "name": "SIP-T58", "dssKeyCount": 27, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t58a", "name": "SIP-T58A", "dssKeyCount": 27, "lignesPosition": 1, "maxAccounts": 16 },
				{ "id": "t58w", "name": "SIP-T58W", "dssKeyCount": 27, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t73u", "name": "SIP-T73U", "dssKeyCount": 40, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t73w", "name": "SIP-T73W", "dssKeyCount": 40, "lignesPosition": 5, "maxAccounts": 12 },
				{ "id": "t74u", "name": "SIP-T74U", "dssKeyCount": 50, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t74w", "name": "SIP-T74W", "dssKeyCount": 50, "lignesPosition": 6, "maxAccounts": 16 },
				{ "id": "t77u", "name": "SIP-T77U", "dssKeyCount": 84, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t85w", "name": "SIP-T85W", "dssKeyCount": 60, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t87w", "name": "SIP-T87W", "dssKeyCount": 84, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t88v", "name": "SIP-T88V", "dssKeyCount": 84, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t88w", "name": "SIP-T88W", "dssKeyCount": 84, "lignesPosition": 7, "maxAccounts": 16 },
				{ "id": "t64lte", "name": "T64LTE", "dssKeyCount": 30, "lignesPosition": 1, "maxAccounts": 16 },
				{ "id": "t67lte", "name": "T67LTE", "dssKeyCount": 21, "lignesPosition": 1, "maxAccounts": 16 },
				{ "id": "vp59", "name": "VP59", "dssKeyCount": 27, "lignesPosition": 1, "maxAccounts": 16 },
				{ "id": "w60b", "name": "W60B", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 8 },
				{ "id": "w70b", "name": "W70B", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 10 },
				{ "id": "w75b", "name": "W75B", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 20 },
				{ "id": "w80b", "name": "W80B", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 100 },
				{ "id": "w90b", "name": "W90B", "dssKeyCount": 0, "lignesPosition": 0, "maxAccounts": 250 }
			],

			ringTypeOptionsAll: [
				"Common",
				"Ring1.wav",
				"Ring2.wav",
				"Ring3.wav",
				"Ring4.wav",
				"Ring5.wav",
				"Ring6.wav",
				"Ring7.wav",
				"Ring8.wav",
				"Silent.wav",
				"Splash.wav"
			],

			ringTypeOptionsT3x: [
				"Common",
				"Ring1.wav",
				"Ring2.wav",
				"Ring3.wav",
				"Ring4.wav",
				"Silent.wav",
				"Splash.wav"
			],

			dssKeyTypes: [
				{ value: 0, text: "N/A" },
				{ value: -1, text: "Line" },
				{ value: 13, text: "Speed Dial" },
				{ value: 24, text: "Intercom" },
				{ value: 27, text: "XML Browser" }
			],

			dssKeys: [],

			additionalAccounts: [],

			handsets: [
				{ index: 1, account: 1 }
			],

			timezoneOptions: [
				{ text: "Canada (Montreal, Ottawa, Quebec) UTC-5", value: -5 },
				{ text: "Canada (Toronto) UTC-5", value: -5 },
				{ text: "Canada (Vancouver) UTC-8", value: -8 },
				{ text: "Canada (Winnipeg) UTC-6", value: -6 },
				{ text: "Canada (Calgary, Edmonton) UTC-7", value: -7 }
			],

			natTraversalOptions: [
				{ text: "Disabled", value: 0 },
				{ text: "STUN", value: 1 },
				{ text: "ICE", value: 2 }
			],

			transportTypeOptions: [
				{ text: "UDP", value: 0 },
				{ text: "TCP", value: 1 },
				{ text: "TLS", value: 2 },
				{ text: "DNS-NAPTR", value: 3 }
			],

			guiLanguageOptions: [
				{ text: "French (Canada)", value: "French_CA" },
				{ text: "English", value: "English" },
				{ text: "Chinese (Simplified)", value: "Chinese_S" },
				{ text: "Chinese (Traditional)", value: "Chinese_T" },
				{ text: "French", value: "French" },
				{ text: "Spanish", value: "Spanish" }
			],

			macRules: [
				v => !v || v.length === 0 || /^([0-9A-Fa-f]{2}[:-]?){5}[0-9A-Fa-f]{2}$/.test(v.replace(/[:-]/g, "").match(/.{1,2}/g)?.join(":") || "") || this.$t("yealink-config-invalid-mac")
			],

			portRules: [
				v => !v || v <= 65535 || this.$t("yealink-config-port-max-error")
			],

			vlanRules: [
				v => !v || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 4094) || this.$t("yealink-config-vlan-invalid")
			]
		};
	},

	computed: {
		canGenerate() {
			return this.selectedSipUser && this.selectedModel;
		},

		isWirelessModel() {
			return /^w/.test(this.selectedModel?.id);
		},

		dssKeyLengthOptions() {
			return [
				{ text: this.$t("yealink-config-dss-length-default"), value: "0" },
				{ text: this.$t("yealink-config-dss-length-quarter"), value: "2" },
				{ text: this.$t("yealink-config-dss-length-half"), value: "1" }
			];
		},

		intercomEnabled: {
			get() {
				return this.dssKeys.some(k => k.type === 24);
			},

			set() {
				// handled by onIntercomToggle
			}
		},

		deviceMacOptions() {
			return this.devices.map(d => ({
				text: d.name || d.mac,
				value: d.mac,
				modelName: d.modelName || "",
				status: this.deviceStatusColor(d.deviceStatus)
			})).sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: "base" }) || a.value.localeCompare(b.value, undefined, { sensitivity: "base" }));
		},

		rtpMaxPort() {
			return this.rtpMinPort + 1000;
		},

		expectedSipPort() {
			if (!this.selectedSipUser) return 5080;
			const index = this.sipUsers.findIndex(u => u.username === this.selectedSipUser.username);

			return index !== -1 ? 5080 + index : 5080;
		},

		expectedRtpMinPort() {
			if (!this.selectedSipUser) return 11780;
			const index = this.sipUsers.findIndex(u => u.username === this.selectedSipUser.username);

			return index !== -1 ? 11780 + (index * 2000) : 11780;
		},

		filename() {
			if (this.macAddress) {
				// Handle both string and object (from combobox selection)
				const mac = typeof this.macAddress === "string" ? this.macAddress : this.macAddress.value;

				if (mac) {
					const cleanMac = mac.replace(/[:-]/g, "").toLowerCase();

					return `${cleanMac}.cfg`;
				}
			}
			return `${this.selectedSipUser?.username || "config"}.cfg`;
		},

		otherSipUsers() {
			if (!this.selectedSipUser) return [];
			return this.sipUsers
				.filter(u => u.username !== this.selectedSipUser.username)
				.sort((a, b) => a.username.localeCompare(b.username, undefined, { numeric: true }));
		},

		maxDssKeys() {
			return this.selectedModel?.dssKeyCount || 2;
		},

		maxAdditionalAccounts() {
			if (!this.selectedModel) return 0;
			return (this.selectedModel.maxAccounts || 1) - 1; // subtract 1 for main account
		},

		maxHandsets() {
			return this.selectedModel?.maxAccounts || 1;
		},

		handsetAccountOptions() {
			const options = [];
			// Account 1 = main account

			if (this.selectedSipUser) {
				const name = this.displayName || this.selectedSipUser.displayName || "";

				options.push({ value: 1, text: `${name} (${this.selectedSipUser.username})` });
			} else {
				options.push({ value: 1, text: "1" });
			}
			// Additional accounts
			this.additionalAccounts.forEach((acc, idx) => {
				if (acc.sipUser) {
					const name = acc.displayName || acc.sipUser.displayName || "";

					options.push({ value: idx + 2, text: `${name} (${acc.sipUser.username})` });
				}
			});
			return options;
		},

		ringTypeOptions() {
			if (!this.selectedModel || this.isWirelessModel) return [];
			if (this.selectedModel.id.startsWith("t3")) return this.ringTypeOptionsT3x;
			return this.ringTypeOptionsAll;
		},

		availableSipUsersForAdditional() {
			if (!this.selectedSipUser) return [];
			const usedUsernames = new Set([
				this.selectedSipUser.username,
				...this.additionalAccounts.map(a => a.sipUser?.username).filter(Boolean)
			]);

			return this.sipUsers.filter(u => !usedUsernames.has(u.username));
		},

		selectedDeviceId() {
			if (!this.macAddress) return null;
			// Handle both string and object (from combobox selection)
			const mac = typeof this.macAddress === "string" ? this.macAddress : this.macAddress.value;

			if (!mac) return null;
			const cleanMac = mac.replace(/[:-]/g, "").toLowerCase();
			const device = this.devices.find(d => d.mac?.replace(/[:-]/g, "").toLowerCase() === cleanMac);

			return device?.id || null;
		},

		configPreview() {
			return this.generateConfig();
		},

		currentConfigMap() {
			return this.parseConfigToMap(this.currentConfig);
		},

		generatedConfigMap() {
			return this.parseConfigToMap(this.configPreview);
		},

		configPreviewData() {
			if (!this.configPreview) return { html: "", diff: null };

			const currentMap = this.currentConfigMap;
			const generatedMap = this.generatedConfigMap;
			const hasDiff = !!this.currentConfig;

			let addedCount = 0;
			let modifiedCount = 0;
			let unchangedCount = 0;

			// Build array of { html, key } for positional insertion of removed keys
			const outputLines = [];

			for (const line of this.configPreview.split("\n")) {
				const trimmed = line.trim();

				if (!trimmed) {
					outputLines.push({ html: "<div class=\"cfg-line\"> </div>", key: null });
					continue;
				}

				let lineKey = null;
				let diffClass = "";
				let diffAnnotation = "";

				// Extract key and determine diff status
				const eqIdx = trimmed.indexOf(" = ");
				const eqEnd = trimmed.endsWith(" =");

				if (eqIdx !== -1 || eqEnd) {
					lineKey = eqIdx !== -1 ? trimmed.substring(0, eqIdx) : trimmed.substring(0, trimmed.length - 2);
					const newValue = eqIdx !== -1 ? trimmed.substring(eqIdx + 3) : "";

					if (hasDiff) {
						if (!(lineKey in currentMap)) {
							diffClass = " cfg-line-added";
							addedCount++;
						} else if (currentMap[lineKey] !== newValue) {
							diffClass = " cfg-line-modified";
							modifiedCount++;
							diffAnnotation = `<span class="cfg-old-value"> \u2190 ${this.escapeHtml(currentMap[lineKey] || "(empty)")}</span>`;
						} else {
							diffClass = " cfg-line-unchanged";
							unchangedCount++;
						}
					}
				}

				let highlighted;

				if (trimmed.startsWith("#!version")) {
					highlighted = `<span class="cfg-directive">${this.escapeHtml(line)}</span>`;
				} else if (trimmed.startsWith("#")) {
					highlighted = `<span class="cfg-comment">${this.escapeHtml(line)}</span>`;
				} else if (eqIdx !== -1) {
					const key = line.substring(0, line.indexOf(" = "));
					const value = line.substring(line.indexOf(" = ") + 3);

					highlighted = `<span class="cfg-key">${this.escapeHtml(key)}</span><span class="cfg-equals"> = </span><span class="cfg-value">${this.escapeHtml(value)}</span>${diffAnnotation}`;
				} else if (eqEnd) {
					const key = line.substring(0, line.lastIndexOf(" ="));

					highlighted = `<span class="cfg-key">${this.escapeHtml(key)}</span><span class="cfg-equals"> =</span>${diffAnnotation}`;
				} else {
					highlighted = this.escapeHtml(line);
				}

				outputLines.push({ html: `<div class="cfg-line${diffClass}">${highlighted}</div>`, key: lineKey });
			}

			// Compute removed keys and insert inline
			let removedKeys = [];

			if (hasDiff) {
				removedKeys = Object.keys(currentMap).filter(k => !(k in generatedMap)).sort();

				for (const rKey of removedKeys) {
					const parts = rKey.split(".");
					let insertIdx = -1;

					for (let depth = parts.length; depth >= 1; depth--) {
						const prefix = parts.slice(0, depth).join(".");

						for (let i = outputLines.length - 1; i >= 0; i--) {
							if (outputLines[i].key && outputLines[i].key.startsWith(prefix)) {
								insertIdx = i + 1;
								break;
							}
						}
						if (insertIdx !== -1) break;
					}

					const removedHtml = `<div class="cfg-line cfg-line-removed"><span class="cfg-key">${this.escapeHtml(rKey)}</span><span class="cfg-equals"> = </span><span class="cfg-value">${this.escapeHtml(currentMap[rKey])}</span></div>`;

					if (insertIdx === -1) {
						outputLines.push({ html: removedHtml, key: rKey });
					} else {
						outputLines.splice(insertIdx, 0, { html: removedHtml, key: rKey });
					}
				}
			}

			const html = `<div class="cfg-content">${outputLines.map(l => l.html).join("")}</div>`;
			const diff = hasDiff ? { added: addedCount, modified: modifiedCount, unchanged: unchangedCount, removed: removedKeys.length } : null;

			return { html, diff };
		},

		configDiff() {
			return this.configPreviewData.diff;
		},

		configPreviewHighlighted() {
			return this.configPreviewData.html;
		}
	},

	watch: {
		loading(val) {
			this.$emit("update:loading", val);
		},

		selectedDeviceId(newVal, oldVal) {
			if (newVal && newVal !== oldVal) {
				// Try to auto-match SIP user based on device name
				const device = this.devices.find(d => d.id === newVal);

				if (device?.name) {
					const deviceNameLower = device.name.toLowerCase();
					const matchedSipUser = this.sipUsers.find(u => u.username.toLowerCase() === deviceNameLower);

					if (matchedSipUser) {
						this.selectedSipUser = matchedSipUser;
					}
				}
				this.fetchCurrentConfig();
			} else if (!newVal) {
				// Invalidate any in-flight request so a late resolution can't apply stale config.
				this.currentConfigRequestId++;
				if (this.currentConfigPollInterval) {
					clearInterval(this.currentConfigPollInterval);
					this.currentConfigPollInterval = null;
				}
				this.currentConfig = null;
				this.currentConfigLoading = false;
				this.currentConfigError = null;
			}
		},

		selectedSipUser: {
			handler(val) {
				if (val) {
					this.updatePortsFromExtension(val.username);
					this.displayName = val.displayName;
					// Restore or reset password state based on sipUser flag
					this.showPasswordInput = false;
					this.manualPassword = "";
					this.passwordManuallySet = !!val._passwordManuallySet;
					// Regenerate DSS keys
					this.generateDefaultDssKeys();
				}
			}
		},

		selectedModel: {
			handler(val) {
				if (val && val.dssKeyCount === 0) {
					this.dssKeysEnabled = false;
				}
				// Trim additional accounts if model has fewer max accounts
				if (val && this.additionalAccounts.length > (val.maxAccounts || 1) - 1) {
					this.additionalAccounts = this.additionalAccounts.slice(0, (val.maxAccounts || 1) - 1);
				}
				this.generateDefaultDssKeys();
			}
		}
	},

	created() {
		this.fetchConfigData();
	},

	beforeDestroy() {
		if (this.currentConfigPollInterval) {
			clearInterval(this.currentConfigPollInterval);
		}
	},

	methods: {
		openSipPanel() {
			this.$emit("open-sip-panel");
		},

		openYealinkPanel() {
			this.$emit("open-yealink-panel");
		},

		async fetchConfigData(options) {
			const silent = options?.silent || false;
			let success = true;

			if (!silent) {
				this.loading = true;
				this.error = null;
			}

			try {
				const response = await this.axios.get("/devops/yealink/phone-config");

				this.configData = response.data;
				this.sipUsers = (response.data.sipUsers || []).sort((a, b) =>
					a.username.localeCompare(b.username, undefined, { numeric: true })
				);
				this.devices = response.data.devices || [];
			} catch (err) {
				console.error("Failed to fetch config data:", err);
				success = false;
				if (!silent) {
					this.error = err.response?.data?.message || this.$t("yealink-config-fetch-error");
				}
			} finally {
				if (!silent) this.loading = false;
				this.$emit("refreshed");
			}

			return { success };
		},

		updatePortsFromExtension(username) {
			// Calculate ports based on position in sorted sipUsers array
			const index = this.sipUsers.findIndex(u => u.username === username);

			if (index !== -1) {
				this.sipPort = 5080 + index;
				this.rtpMinPort = 11780 + (index * 2000);
			}
		},

		generateDefaultDssKeys() {
			if (!this.selectedSipUser || !this.selectedModel) {
				this.dssKeys = [];
				return;
			}

			const keys = [];
			const model = this.selectedModel;
			const maxKeys = model.dssKeyCount;
			const lignesPosition = model.lignesPosition || 1;

			let keyIndex = 1;

			// Build set of usernames excluded from DSS (additional accounts with excludeFromDss checked)
			const excludedFromDss = new Set(
				this.additionalAccounts
					.filter(a => a.sipUser && a.excludeFromDss)
					.map(a => a.sipUser.username)
			);

			// Build the list of users for DSS speed dials
			// otherSipUsers already excludes the main account, so add it back if not excluded
			let dssUsers = [...this.otherSipUsers];

			if (!this.excludeFromDss && this.selectedSipUser) {
				dssUsers.unshift(this.selectedSipUser);
			}

			// Add speed dials for extensions, skipping the lignes position
			for (const user of dssUsers) {
				if (keyIndex >= maxKeys) break;
				// Skip users excluded from DSS
				if (excludedFromDss.has(user.username)) continue;
				// Skip lignes position - reserved for XML browser
				if (keyIndex === lignesPosition) keyIndex++;
				if (keyIndex >= maxKeys) break;

				keys.push({
					index: keyIndex,
					type: 13, // Speed dial
					label: user.displayName || user.username,
					value: user.username,
					line: 0
				});
				keyIndex++;
			}

			// Add XML browser at lignes position
			keys.push({
				index: lignesPosition,
				type: 27, // XML browser
				label: "Lignes",
				value: this.configData?.parkedCallsUrlBase || "",
				line: 0
			});

			// Sort by index
			keys.sort((a, b) => a.index - b.index);

			this.dssKeys = keys;
			this.padEmptyIndexes();
		},

		addDssKey() {
			if (this.dssKeys.length >= this.maxDssKeys) return;
			const nextIndex = this.dssKeys.length > 0
				? Math.max(...this.dssKeys.map(k => k.index)) + 1
				: 1;

			this.dssKeys.push({
				index: nextIndex,
				type: 13, // Speed dial
				label: "",
				value: "",
				line: 0
			});
			this.padEmptyIndexes();
		},

		addHandset() {
			const nextIndex = this.handsets.length > 0
				? Math.max(...this.handsets.map(h => h.index)) + 1
				: 1;

			this.handsets.push({ index: nextIndex, account: 1 });
		},

		removeHandset(idx) {
			if (idx === 0) return;
			this.handsets.splice(idx, 1);
			this.handsets.forEach((hs, i) => {
				hs.index = i + 1;
			});
		},

		addAdditionalAccount() {
			if (this.additionalAccounts.length >= this.maxAdditionalAccounts) return;
			this.additionalAccounts.push({
				sipUser: null,
				displayName: "",
				ringType: "Common",
				excludeFromDss: true,
				showPasswordInput: false,
				manualPassword: "",
				passwordManuallySet: false
			});
		},

		removeAdditionalAccount(idx) {
			this.additionalAccounts.splice(idx, 1);
			this.generateDefaultDssKeys();
		},

		onAdditionalAccountChange(idx) {
			const acc = this.additionalAccounts[idx];

			if (acc.sipUser) {
				acc.displayName = acc.sipUser.displayName || "";
			}
			// Restore or reset password state based on sipUser flag
			acc.showPasswordInput = false;
			acc.manualPassword = "";
			acc.passwordManuallySet = !!(acc.sipUser && acc.sipUser._passwordManuallySet);
			this.generateDefaultDssKeys();
		},

		getAvailableSipUsersFor(idx) {
			if (!this.selectedSipUser) return [];
			const usedUsernames = new Set([
				this.selectedSipUser.username,
				...this.additionalAccounts
					.filter((a, i) => i !== idx && a.sipUser)
					.map(a => a.sipUser.username)
			]);

			return this.sipUsers.filter(u => !usedUsernames.has(u.username));
		},

		removeDssKey(idx) {
			this.dssKeys.splice(idx, 1);
			// Re-index
			this.dssKeys.forEach((key, i) => {
				key.index = i + 1;
			});
			this.padEmptyIndexes();
		},

		onDragEnd() {
			// Re-index after drag
			this.dssKeys.forEach((key, i) => {
				key.index = i + 1;
			});
			this.padEmptyIndexes();
		},

		resetDssKeys() {
			this.generateDefaultDssKeys();
		},

		isLignesKey(key) {
			return key.type === 27 && key.label === "Lignes";
		},

		padEmptyIndexes() {
			if (!this.selectedModel) return;

			// Find the Lignes key
			const lignesKey = this.dssKeys.find(k => this.isLignesKey(k));

			if (!lignesKey) return;

			const lignesPosition = this.selectedModel.lignesPosition || 1;

			// Remove Lignes from array temporarily
			this.dssKeys = this.dssKeys.filter(k => !this.isLignesKey(k));

			// Ensure we have enough keys before Lignes position (pad with N/A)
			while (this.dssKeys.length < lignesPosition - 1) {
				this.dssKeys.push({
					index: this.dssKeys.length + 1,
					type: 0, // N/A
					label: "",
					value: "",
					line: 0
				});
			}

			// Insert Lignes at the correct position
			this.dssKeys.splice(lignesPosition - 1, 0, lignesKey);

			// Re-index all keys
			this.dssKeys.forEach((key, i) => {
				key.index = i + 1;
			});
		},

		onIntercomToggle(checked) {
			if (checked) {
				// Replace first N/A key if one exists, otherwise add at the bottom
				const naKey = this.dssKeys.find(k => k.type === 0);

				if (naKey) {
					naKey.type = 24;
					naKey.label = "Intercom";
					naKey.value = "224.0.1.116:5001";
					naKey.line = 0;
					naKey.extension = 0;
				} else {
					if (this.dssKeys.length >= this.maxDssKeys) return;
					const nextIndex = this.dssKeys.length > 0
						? Math.max(...this.dssKeys.map(k => k.index)) + 1
						: 1;

					this.dssKeys.push({
						index: nextIndex,
						type: 24,
						label: "Intercom",
						value: "224.0.1.116:5001",
						line: 0,
						extension: 0
					});
					this.padEmptyIndexes();
				}
			} else {
				// Remove all intercom keys
				this.dssKeys = this.dssKeys.filter(k => k.type !== 24);
				// Re-index
				this.dssKeys.forEach((key, i) => {
					key.index = i + 1;
				});
				this.padEmptyIndexes();
			}
		},

		onDssTypeChange(key) {
			if (key.type === 0) {
				// N/A type: clear all fields
				key.label = "";
				key.value = "";
				key.line = 0;
			} else if (key.type === -1) {
				// Line type: force value to 0, set default line to 1, set label to display name
				key.value = 0;
				key.line = 1;
				key.label = this.selectedSipUser?.displayName || "";
			} else if (key.type === 24) {
				// Intercom type: set fixed values
				key.label = "Intercom";
				key.value = "224.0.1.116:5001";
				key.line = 0;
				key.extension = 0;
			} else {
				// Non-line type: reset line to 0
				key.line = 0;
				delete key.extension;
				// Speed dial: blank value
				if (key.type === 13) {
					key.value = "";
				}
			}
		},

		formatMacAddress(val) {
			if (!val) return;
			// Handle object from combobox selection
			if (typeof val === "object" && val.value) {
				this.macAddress = val.value.replace(/[:-]/g, "").toLowerCase();
				return;
			}
			if (typeof val !== "string") return;
			// Clean and format MAC address
			const clean = val.replace(/[^0-9A-Fa-f]/g, "").toLowerCase();

			if (clean.length === 12) {
				this.macAddress = clean;
			}
		},

		deviceStatusColor(status) {
			if (!status) return "grey";
			const s = status.toLowerCase();

			if (s === "online") return "green";
			if (s === "offline") return "red";
			return "grey";
		},

		onMacAddressChange(val) {
			this.formatMacAddress(val);
			// Auto-match device model to phone model dropdown
			if (!val) return;
			const mac = typeof val === "string" ? val : val.value;

			if (!mac) return;
			const cleanMac = mac.replace(/[:-]/g, "").toLowerCase();
			const device = this.devices.find(d => d.mac?.replace(/[:-]/g, "").toLowerCase() === cleanMac);

			if (device?.modelName) {
				const matched = this.matchModelFromName(device.modelName);

				if (matched) {
					this.selectedModel = matched;
				}
			}
		},

		matchModelFromName(modelName) {
			if (!modelName) return null;
			const normalized = modelName.trim().toLowerCase();

			return this.phoneModels.find(m => m.name.toLowerCase() === normalized) || null;
		},

		generateConfig() {
			if (!this.selectedSipUser || !this.selectedModel) return "";

			const user = this.selectedSipUser;
			const sipServer = this.configData?.sipServer;
			const stunServer = this.configData?.stunServer;
			const mohUri = this.configData?.mohUri;

			// Build list of all accounts (main + additional)
			const accounts = [
				{ index: 1, username: user.username, displayName: this.displayName || user.displayName || user.username, password: user.password || "", ringType: this.ringType }
			];

			for (let i = 0; i < this.additionalAccounts.length; i++) {
				const acc = this.additionalAccounts[i];

				if (!acc.sipUser) continue;
				accounts.push({
					index: i + 2,
					username: acc.sipUser.username,
					displayName: acc.displayName || acc.sipUser.displayName || acc.sipUser.username,
					password: acc.sipUser.password || "",
					ringType: acc.ringType || "Common"
				});
			}

			const accountConfig = accounts.map(acc =>
				[
					`account.${acc.index}.100rel_enable = 1`,
					`account.${acc.index}.auth_name = ${acc.username}`,
					`account.${acc.index}.cid_source = 5`,
					`account.${acc.index}.display_name = ${acc.displayName}`,
					`account.${acc.index}.earlymedia = 1`,
					`account.${acc.index}.enable = 1`,
					`account.${acc.index}.label = ${acc.displayName}`,
					`account.${acc.index}.missed_calllog = 0`,
					`account.${acc.index}.music_server_uri = ${mohUri}`,
					`account.${acc.index}.nat.nat_traversal = ${this.natTraversal}`,
					`account.${acc.index}.nat.udp_update_enable = 2`,
					`account.${acc.index}.nat.udp_update_time = 5`,
					`account.${acc.index}.password = ${acc.password}`,
					`account.${acc.index}.sip_server.1.address = ${sipServer}`,
					`account.${acc.index}.sip_server.1.port = 5060`,
					`account.${acc.index}.sip_server.1.transport_type = ${this.transportType}`,
					...(!this.isWirelessModel ? [`account.${acc.index}.ringtone.ring_type = ${acc.ringType}`] : []),
					`account.${acc.index}.user_name = ${acc.username}`
				].join("\n")
			).join("\n");

			let config = `#!version:1.0.0.1

				### This file is the exported MAC-all.cfg.

				${accountConfig}`;

			config += `
				call_waiting.tone = 0
				default_input_method.advanced_setting_password = 2aB
				dm.file_upload.http_method = 1
				dm.file_upload.http_post_mode = 0
				features.acoustic_shield.mode = 2
				features.caller_name_type_on_dialing = 1
				features.call_park.enable = 1
				features.call_park.park_code = park
				features.call_park.park_mode = 2
				transfer.hang_up_after_success_trans = 3
				features.relog_offtime = 1000
				lang.gui = ${this.guiLanguage}`;

			// Add line keys (skip N/A type)
			if (this.dssKeysEnabled) {
				config += `\nfeatures.config_dsskey_length = ${this.dssKeyLength}`;

				for (const key of this.dssKeys) {
					if (key.type === 0) {
						// N/A: reset the key slot
						config += `
							linekey.${key.index}.type = 0
							linekey.${key.index}.line = 0
							linekey.${key.index}.extension = %NULL%
							linekey.${key.index}.label = %NULL%
							linekey.${key.index}.value = %NULL%`;

						continue;
					}

					config += `
						linekey.${key.index}.label = ${key.label || "%NULL%"}
						linekey.${key.index}.line = ${key.line != null && key.line !== "" ? key.line : "%NULL%"}
						linekey.${key.index}.extension = ${key.type === 24 ? (key.extension ?? 0) : "%NULL%"}
						linekey.${key.index}.type = ${key.type === -1 ? "%NULL%" : key.type}
						linekey.${key.index}.value = ${key.value != null && key.value !== "" ? key.value : "%NULL%"}`;
				}
			}

			// Add handset config for W-series
			if (this.isWirelessModel) {
				for (const hs of this.handsets) {
					config += `
						handset.${hs.index}.incoming_lines = ${hs.account}
						handset.${hs.index}.dial_out_default_line = ${hs.account}
						handset.${hs.index}.dial_out_lines = ${hs.account}`;
				}
			}

			// Add remaining settings
			config += `
				local_time.date_format = 5
				local_time.time_zone = ${this.timezone}
				local_time.time_zone_name = Canada(Montreal,Ottawa,Quebec)
				local_time.summer_time = 2
				multicast.listen_address.1.ip_address = 224.0.1.116:5001
				multicast.listen_address.1.label = Intercom
				multicast.receive.ignore_dnd.priority = 1
				phone_setting.custom_headset_mode_status = 1
				phone_setting.custom_softkey_enable = 1
				custom_softkey_talking.url = http://storage.googleapis.com/bucco-api/Talking.xml
				phone_setting.page_tip = 1
				sip.listen_port = ${this.sipPort}
				###  Static Configuration  ###
				static.auto_provision.local_log.backup.append = 1
				static.auto_provision.local_log.backup.append.limit_mode = 1
				static.auto_provision.local_log.backup.append.max_file_size = 512
				static.dm.enable = 1
				static.dm.server.address = us-device-scheduler.ymcs.yealink.com
				static.network.port.max_rtpport = ${this.rtpMaxPort}
				static.network.port.min_rtpport = ${this.rtpMinPort}
				${this.wanVlanId ? `static.network.vlan.internet_port_enable = 1
				static.network.vlan.internet_port_vid = ${this.wanVlanId}` : `static.network.vlan.internet_port_enable = 0
				static.network.vlan.internet_port_vid = %NULL%`}
				static.remote_control.1.allow = 1
				static.remote_control.3.allow = 1
				static.sip.nat_stun.enable = ${this.natTraversal === 1 ? 1 : 0}
				static.sip.nat_stun.server = ${stunServer}
				static.security.user_password = ${this.configData?.adminPassword || ""}
				static.security.user_password = ${this.configData?.userPassword || ""}
				`;

			// Strip leading whitespace from each line (from template indentation)
			return config.split("\n").map(line => line.trim()).join("\n");
		},

		downloadConfig() {
			const config = this.generateConfig();
			const blob = new Blob([config], { type: "text/plain" });
			const url = URL.createObjectURL(blob);
			const link = document.createElement("a");

			link.href = url;
			link.download = this.filename;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			this.$success(this.$t("yealink-config-download-success"));
			this.resetForm();
		},

		async pushToYmcs() {
			if (!this.selectedDeviceId) return;

			// Gate: if the current phone config diff hasn't loaded yet, prompt the user.
			if (this.currentConfigLoading && !this.deployWithoutDiffConfirmed) {
				this.deployGateDialog = true;
				return;
			}

			this.deployWithoutDiffConfirmed = false;
			this.pushing = true;
			try {
				const config = this.generateConfig();

				await this.axios.post("/devops/yealink/phone-config/push", {
					deviceId: this.selectedDeviceId,
					configData: config
				});
				this.$success(this.$t("yealink-config-push-ymcs-success"));
				this.resetForm();
			} catch (err) {
				console.error("Failed to push config to YMCS:", err);
				this.$error(this.$t("yealink-config-push-ymcs-error"));
			} finally {
				this.pushing = false;
			}
		},

		confirmDeployWithoutDiff() {
			this.disarmDeployConfirm();
			this.deployGateDialog = false;
			this.deployWithoutDiffConfirmed = true;
			this.pushToYmcs();
		},

		onDeployGateClick() {
			if (this.deployConfirmArmed) {
				this.confirmDeployWithoutDiff();
				return;
			}
			this.deployConfirmArmed = true;
			if (this.deployConfirmTimer) clearTimeout(this.deployConfirmTimer);
			this.deployConfirmTimer = setTimeout(() => {
				this.disarmDeployConfirm();
			}, 5000);
		},

		disarmDeployConfirm() {
			this.deployConfirmArmed = false;
			if (this.deployConfirmTimer) {
				clearTimeout(this.deployConfirmTimer);
				this.deployConfirmTimer = null;
			}
		},

		cancelDeployGate() {
			this.disarmDeployConfirm();
			this.deployGateDialog = false;
			this.deployWithoutDiffConfirmed = false;
		},

		resetForm() {
			this.selectedSipUser = null;
			this.selectedModel = null;
			this.macAddress = "";
			this.displayName = "";
			this.sipPort = 5080;
			this.rtpMinPort = 11780;
			this.natTraversal = 1;
			this.transportType = 1;
			this.timezone = -5;
			this.guiLanguage = "French_CA";
			this.ringType = "Common";
			this.wanVlanId = "";
			this.excludeFromDss = true;
			this.handsets = [{ index: 1, account: 1 }];
			this.additionalAccounts = [];
			this.dssKeysEnabled = true;
			this.showPasswordInput = false;
			this.manualPassword = "";
			this.passwordManuallySet = false;
			this.currentConfig = null;
			this.currentConfigLoading = false;
			this.currentConfigError = null;
			// Invalidate any in-flight export/poll/download so their async resolutions are dropped.
			this.currentConfigRequestId++;
			if (this.currentConfigPollInterval) {
				clearInterval(this.currentConfigPollInterval);
				this.currentConfigPollInterval = null;
			}
			this.networkDiffDialog = false;
			this.networkDiffs = [];
			this.networkDiffOriginals = null;
			this.disarmRevertConfirm();
			this.deployGateDialog = false;
			this.deployWithoutDiffConfirmed = false;
			this.disarmDeployConfirm();
		},

		applyManualPassword() {
			if (this.selectedSipUser && this.manualPassword) {
				this.selectedSipUser.password = this.manualPassword;
				this.$set(this.selectedSipUser, "_passwordManuallySet", true);
				this.passwordManuallySet = true;
				this.showPasswordInput = false;
				this.manualPassword = "";
			}
		},

		clearManualPassword() {
			if (this.selectedSipUser) {
				this.selectedSipUser.password = "";
				this.$set(this.selectedSipUser, "_passwordManuallySet", false);
				this.passwordManuallySet = false;
			}
		},

		applyAdditionalPassword(idx) {
			const acc = this.additionalAccounts[idx];

			if (acc?.sipUser && acc.manualPassword) {
				this.$set(acc.sipUser, "password", acc.manualPassword);
				this.$set(acc.sipUser, "_passwordManuallySet", true);
				this.$set(acc, "passwordManuallySet", true);
				this.$set(acc, "showPasswordInput", false);
				this.$set(acc, "manualPassword", "");
			}
		},

		clearAdditionalPassword(idx) {
			const acc = this.additionalAccounts[idx];

			if (acc?.sipUser) {
				this.$set(acc.sipUser, "password", "");
				this.$set(acc.sipUser, "_passwordManuallySet", false);
				this.$set(acc, "passwordManuallySet", false);
			}
		},

		async fetchCurrentConfig() {
			const deviceId = this.selectedDeviceId;

			if (!deviceId) return;

			// Clear previous state
			if (this.currentConfigPollInterval) {
				clearInterval(this.currentConfigPollInterval);
				this.currentConfigPollInterval = null;
			}
			this.currentConfig = null;
			this.currentConfigLoading = true;
			this.currentConfigError = null;

			// Bump generation token; any in-flight request from a previous device/reset becomes stale.
			const requestId = ++this.currentConfigRequestId;

			try {
				const response = await this.axios.post(
					`/devops/yealink/devices/${deviceId}/export-config`
				);

				if (requestId !== this.currentConfigRequestId) return;
				this.currentConfigDiagnosisId = response.data.diagnosisId;
				this.pollCurrentConfigStatus(requestId);
			} catch (err) {
				if (requestId !== this.currentConfigRequestId) return;
				console.error("Failed to start config export:", err);
				this.currentConfigError = err.response?.data?.message || this.$t("yealink-config-diff-error");
				this.currentConfigLoading = false;
			}
		},

		pollCurrentConfigStatus(requestId) {
			if (this.currentConfigPollInterval) {
				clearInterval(this.currentConfigPollInterval);
			}
			this.currentConfigPollInterval = setInterval(async () => {
				if (requestId !== this.currentConfigRequestId) {
					clearInterval(this.currentConfigPollInterval);
					this.currentConfigPollInterval = null;
					return;
				}
				try {
					const response = await this.axios.get(
						`/devops/yealink/diagnosis/${this.currentConfigDiagnosisId}/status`
					);

					if (requestId !== this.currentConfigRequestId) {
						clearInterval(this.currentConfigPollInterval);
						this.currentConfigPollInterval = null;
						return;
					}
					if (response.data.status === "success" && response.data.url) {
						clearInterval(this.currentConfigPollInterval);
						this.currentConfigPollInterval = null;
						await this.downloadCurrentConfig(response.data.url, requestId);
					} else if (response.data.status === "failure") {
						clearInterval(this.currentConfigPollInterval);
						this.currentConfigPollInterval = null;
						this.currentConfigError = this.$t("yealink-config-diff-error");
						this.currentConfigLoading = false;
					}
				} catch (err) {
					if (requestId !== this.currentConfigRequestId) return;
					console.error("Failed to poll export status:", err);
					clearInterval(this.currentConfigPollInterval);
					this.currentConfigPollInterval = null;
					this.currentConfigError = this.$t("yealink-config-diff-error");
					this.currentConfigLoading = false;
				}
			}, 3000);
		},

		async downloadCurrentConfig(url, requestId) {
			try {
				const response = await fetch(url);

				if (!response.ok) throw new Error("Failed to download config");
				const text = await response.text();

				// Drop result if the user reset the form or selected another device while the download was in-flight.
				if (requestId !== undefined && requestId !== this.currentConfigRequestId) return;
				this.currentConfig = text;
				// Sync DSS key length with what the phone currently reports, but only when
				// the key is present in the phone config and has a non-zero value.
				const phoneDssKeyLength = this.currentConfigMap["features.config_dsskey_length"];

				if (phoneDssKeyLength != null && phoneDssKeyLength !== "" && phoneDssKeyLength !== "0") {
					this.dssKeyLength = phoneDssKeyLength;
				}
				// If the deploy gate dialog was waiting on the diff, close it now;
				// checkNetworkSettingsDiff() will open the network diff dialog if needed.
				if (this.deployGateDialog) {
					this.deployGateDialog = false;
				}
				this.checkNetworkSettingsDiff();
			} catch (err) {
				if (requestId !== undefined && requestId !== this.currentConfigRequestId) return;
				console.error("Failed to download config file:", err);
				this.currentConfigError = this.$t("yealink-config-diff-error");
			} finally {
				if (requestId === undefined || requestId === this.currentConfigRequestId) {
					this.currentConfigLoading = false;
				}
			}
		},

		parseConfigToMap(configStr) {
			const map = {};

			if (!configStr) return map;
			for (const line of configStr.split("\n")) {
				const trimmed = line.trim();

				if (!trimmed || trimmed.startsWith("#")) continue;

				const eqIdx = trimmed.indexOf(" = ");

				if (eqIdx !== -1) {
					map[trimmed.substring(0, eqIdx)] = trimmed.substring(eqIdx + 3);
				} else if (trimmed.endsWith(" =")) {
					map[trimmed.substring(0, trimmed.length - 2)] = "";
				}
			}
			return map;
		},

		getNatTraversalLabel(value) {
			const opt = this.natTraversalOptions.find(o => String(o.value) === String(value));

			return opt ? `${opt.text} (${value})` : String(value);
		},

		getTransportTypeLabel(value) {
			const opt = this.transportTypeOptions.find(o => String(o.value) === String(value));

			return opt ? `${opt.text} (${value})` : String(value);
		},

		getConfiguredAccountIndexes() {
			const indexes = [1];

			for (let i = 0; i < this.additionalAccounts.length; i++) {
				if (this.additionalAccounts[i].sipUser) indexes.push(i + 2);
			}
			return indexes;
		},

		checkNetworkSettingsDiff() {
			if (!this.currentConfig) return;

			// Phone is only considered configured if account.1.user_name is present and non-empty
			const isPhoneConfigured = !!this.currentConfigMap["account.1.user_name"];

			if (!isPhoneConfigured) return;

			const accountIndexes = this.getConfiguredAccountIndexes();
			const findFirstAccountValue = suffix => {
				for (const idx of accountIndexes) {
					const key = `account.${idx}.${suffix}`;

					if (key in this.currentConfigMap) return this.currentConfigMap[key];
				}
				return null;
			};

			// Resolve effective phone values (defaults applied when absent)
			const phoneNatRaw = findFirstAccountValue("nat.nat_traversal");
			const phoneNat = parseInt(phoneNatRaw !== null ? phoneNatRaw : "0", 10);

			const phoneTransportRaw = findFirstAccountValue("sip_server.1.transport_type");
			const phoneTransport = parseInt(phoneTransportRaw !== null ? phoneTransportRaw : "0", 10);

			const enableKey = "static.network.vlan.internet_port_enable";
			const vidKey = "static.network.vlan.internet_port_vid";
			const enableRaw = enableKey in this.currentConfigMap ? this.currentConfigMap[enableKey] : null;
			const vidRaw = vidKey in this.currentConfigMap ? this.currentConfigMap[vidKey] : "";
			// Treat VLAN as enabled if either the enable flag is "1" OR a non-empty VID is set
			// (the enable key may be omitted when the phone is at its default).
			const phoneVlanEnabled = enableRaw === "1" || (enableRaw === null && !!vidRaw);
			const phoneWanVlanId = phoneVlanEnabled ? vidRaw : "";

			const diffs = [];

			if (this.natTraversal !== phoneNat) {
				diffs.push({
					key: "account.*.nat.nat_traversal",
					previousRaw: this.natTraversal,
					appliedRaw: phoneNat,
					previousLabel: this.getNatTraversalLabel(this.natTraversal),
					appliedLabel: this.getNatTraversalLabel(phoneNat)
				});
			}

			if (this.transportType !== phoneTransport) {
				diffs.push({
					key: "account.*.sip_server.1.transport_type",
					previousRaw: this.transportType,
					appliedRaw: phoneTransport,
					previousLabel: this.getTransportTypeLabel(this.transportType),
					appliedLabel: this.getTransportTypeLabel(phoneTransport)
				});
			}

			const previousWanVlanId = this.wanVlanId || "";

			if (previousWanVlanId !== phoneWanVlanId) {
				diffs.push({
					key: "static.network.vlan.internet_port_*",
					previousRaw: previousWanVlanId,
					appliedRaw: phoneWanVlanId,
					previousLabel: previousWanVlanId ? `${this.$t("enabled")} (${previousWanVlanId})` : this.$t("disabled"),
					appliedLabel: phoneWanVlanId ? `${this.$t("enabled")} (${phoneWanVlanId})` : this.$t("disabled")
				});
			}

			if (diffs.length === 0) return;

			// Save originals so we can revert
			this.networkDiffOriginals = {
				natTraversal: this.natTraversal,
				transportType: this.transportType,
				wanVlanId: this.wanVlanId
			};

			// Auto-apply phone values
			this.natTraversal = phoneNat;
			this.transportType = phoneTransport;
			this.wanVlanId = phoneWanVlanId;

			this.networkDiffs = diffs;
			this.networkDiffDialog = true;
		},

		onNetworkSettingBlur() {
			// Re-check diff when user changes a monitored network setting on a configured phone
			if (this.networkDiffDialog) return;
			if (!this.currentConfig) return;
			this.$nextTick(() => this.checkNetworkSettingsDiff());
		},

		acceptNetworkSettings() {
			this.disarmRevertConfirm();
			this.networkDiffDialog = false;
			this.networkDiffs = [];
			this.networkDiffOriginals = null;
		},

		onRevertClick() {
			if (this.revertConfirmArmed) {
				this.disarmRevertConfirm();
				this.revertNetworkSettings();
				return;
			}
			this.revertConfirmArmed = true;
			if (this.revertConfirmTimer) clearTimeout(this.revertConfirmTimer);
			this.revertConfirmTimer = setTimeout(() => {
				this.disarmRevertConfirm();
			}, 5000);
		},

		disarmRevertConfirm() {
			this.revertConfirmArmed = false;
			if (this.revertConfirmTimer) {
				clearTimeout(this.revertConfirmTimer);
				this.revertConfirmTimer = null;
			}
		},

		revertNetworkSettings() {
			if (this.networkDiffOriginals) {
				this.natTraversal = this.networkDiffOriginals.natTraversal;
				this.transportType = this.networkDiffOriginals.transportType;
				this.wanVlanId = this.networkDiffOriginals.wanVlanId;
			}
			this.networkDiffDialog = false;
			this.networkDiffs = [];
			this.networkDiffOriginals = null;
		},

		escapeHtml(str) {
			return str
				.replace(/&/g, "&amp;")
				.replace(/</g, "&lt;")
				.replace(/>/g, "&gt;")
				.replace(/"/g, "&quot;");
		}
	}
};
</script>

<style scoped>
	.config-content {
		min-height: 300px;
	}

	.network-diff-table::v-deep .v-data-table__wrapper {
		overflow-x: hidden;
	}

	.network-diff-table::v-deep td,
	.network-diff-table::v-deep th {
		white-space: normal;
	}

	.yealink-diff-key {
		font-family: 'Consolas', 'Monaco', monospace;
		font-size: 12px;
		word-break: break-all;
	}

	.yealink-diff-previous {
		color: #9e9e9e;
		text-decoration: line-through;
		word-break: break-word;
	}

	.yealink-diff-applied {
		color: #2e7d32;
		font-weight: 500;
		word-break: break-word;
	}

	.yealink-dialog-card {
		border-radius: 8px;
		overflow: hidden;
	}

	.yealink-dialog-title {
		display: flex;
		align-items: center;
		padding: 14px 20px !important;
		gap: 4px;
	}

	.yealink-dialog-text {
		padding: 20px !important;
		/* Cap line length for comfortable reading regardless of dialog width */
		font-size: 14px;
		line-height: 1.55;
	}

	.yealink-dialog-lead {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: rgba(0, 0, 0, 0.87);
		margin-bottom: 8px;
	}

	.yealink-dialog-body {
		font-size: 14px;
		line-height: 1.55;
		color: rgba(0, 0, 0, 0.78);
		max-width: 62ch;
	}

	.yealink-dialog-scope-note {
		display: flex;
		align-items: center;
		font-size: 12.5px;
		font-style: italic;
		color: rgba(0, 0, 0, 0.6);
		margin-bottom: 6px;
	}

	.yealink-dialog-section-title {
		display: flex;
		align-items: center;
		font-size: 13.5px;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.4px;
		color: rgba(0, 0, 0, 0.72);
		margin-top: 4px;
	}

	.yealink-dialog-list {
		padding-left: 22px;
		margin-bottom: 12px;
		max-width: 62ch;
	}

	.yealink-dialog-list li {
		font-size: 14px;
		line-height: 1.55;
		color: rgba(0, 0, 0, 0.78);
		margin-bottom: 4px;
	}

	.yealink-dialog-list li::marker {
		color: rgba(0, 0, 0, 0.4);
	}

	.yealink-dialog-closing {
		font-size: 13.5px;
		font-style: italic;
		color: rgba(0, 0, 0, 0.6);
		max-width: 62ch;
	}

	.yealink-dialog-actions {
		padding: 12px 20px !important;
		gap: 8px;
	}

	.yealink-dialog-btn {
		text-transform: none;
		letter-spacing: 0.2px;
		font-weight: 600;
	}

	.yealink-dialog-alert::v-deep .v-alert__icon {
		align-self: center;
	}

	@media (max-width: 600px) {
		.network-diff-table::v-deep td,
		.network-diff-table::v-deep th {
			padding: 4px 6px !important;
			font-size: 11px;
		}

		.yealink-dialog-title {
			padding: 12px 14px !important;
		}

		.yealink-dialog-text {
			padding: 14px !important;
		}

		.yealink-dialog-actions {
			padding: 10px 14px !important;
			flex-direction: column-reverse;
			align-items: stretch;
		}

		.yealink-dialog-actions .yealink-dialog-btn {
			margin: 0 !important;
		}

		/* Separation so the destructive button can't be misclicked */
		.yealink-dialog-actions .yealink-dialog-btn-danger {
			margin-top: 12px !important;
			opacity: 0.85;
		}
	}

	.sip-warning-alert::v-deep .v-alert__icon,
	.alert-centered-icon::v-deep .v-alert__icon {
		align-self: center;
	}

	.no-data-state {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 48px;
		gap: 16px;
	}

	.no-data-text {
		color: rgba(0, 0, 0, 0.6);
		font-size: 14px;
	}

	.section-title {
		display: flex;
		align-items: center;
	}

	.dss-preview {
		background: rgba(0, 188, 212, 0.05);
		border-radius: 8px;
		padding: 12px;
	}

	.dss-disabled-message {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		padding: 24px 12px;
		opacity: 0.6;
	}

	.dss-table-scroll {
		overflow-x: auto;
		-webkit-overflow-scrolling: touch;
	}

	.dss-table {
		background: transparent !important;
	}

	.dss-table .dss-input,
	.dss-table .dss-select {
		max-width: 100%;
	}

	.dss-table .dss-input ::v-deep .v-input__slot,
	.dss-table .dss-select ::v-deep .v-input__slot {
		min-height: 28px !important;
		max-height: 28px !important;
		padding: 0 8px !important;
	}

	.dss-table .dss-input ::v-deep input,
	.dss-table .dss-select ::v-deep .v-select__selection {
		font-size: 12px;
	}

	.dss-table .dss-select ::v-deep .v-input__append-inner {
		margin-top: 2px !important;
	}

	.dss-table .dss-select ::v-deep .v-input__slot fieldset {
		padding: 0 !important;
	}

	.dss-table .icon-cell {
		padding: 4px 8px !important;
		width: 32px;
		text-align: center;
	}

	.dss-table .drag-handle {
		cursor: grab;
	}

	.dss-table .drag-handle:active {
		cursor: grabbing;
	}

	.dss-table .drag-icon {
		color: rgba(0, 0, 0, 0.38);
	}

	.dss-table .drag-handle:hover .drag-icon {
		color: rgba(0, 0, 0, 0.6);
	}

	.dss-table .dss-row.sortable-ghost {
		opacity: 0.5;
		background: rgba(0, 188, 212, 0.1);
	}

	.dss-table tbody td {
		padding-top: 6px !important;
		padding-bottom: 6px !important;
	}

	.dss-table thead th {
		padding-top: 6px !important;
		padding-bottom: 6px !important;
	}

	.dss-table .dss-row.sortable-chosen {
		background: rgba(0, 188, 212, 0.08);
	}

	@media (max-width: 600px) {
		.dss-table {
			min-width: 650px;
		}

		.dss-table thead th:nth-child(4),
		.dss-table thead th:nth-child(5) {
			min-width: 160px !important;
		}

		.dss-table thead th:nth-child(6) {
			min-width: 90px !important;
			width: 90px !important;
		}
	}

	.config-preview-box {
		background: #1e1e2e;
		color: #cdd6f4;
		border-radius: 8px;
		padding: 12px 4px;
		font-family: 'Consolas', 'Monaco', monospace;
		font-size: 11px;
		line-height: 1.5;
		overflow-x: auto;
		overflow-y: auto;
		white-space: pre;
	}

	.diff-summary {
		gap: 14px;
		font-size: 12px;
	}

	.diff-badge {
		display: inline-flex;
		align-items: center;
		font-weight: 500;
	}

	.diff-badge-added {
		color: #4caf50;
	}

	.diff-badge-modified {
		color: #ff9800;
	}

	.diff-badge-unchanged {
		color: #9e9e9e;
	}

	.diff-badge-removed {
		color: #f44336;
	}

	.config-actions {
		gap: 8px;
	}

	.device-status-pill {
		display: inline-block;
		width: 8px;
		height: 8px;
		border-radius: 50%;
		flex-shrink: 0;
	}

	.device-status-green {
		background-color: #4caf50;
	}

	.device-status-red {
		background-color: #f44336;
	}

	.device-status-grey {
		background-color: #9e9e9e;
	}

	.mac-hint-link {
		color: #00bcd4;
		cursor: pointer;
		text-decoration: none;
		font-weight: 500;
	}

	.mac-hint-link:hover {
		text-decoration: underline;
	}

	.mac-toggle-icon {
		cursor: pointer;
		opacity: 0.6;
	}

	.mac-toggle-icon:hover {
		opacity: 1;
	}

	.config-section {
		background: rgba(0, 0, 0, 0.02);
		border-radius: 4px;
		padding: 8px 12px;
	}

	.additional-account-row {
		padding: 8px;
		background: rgba(0, 0, 0, 0.02);
		border-radius: 4px;
		border-left: 3px solid rgba(0, 188, 212, 0.4);
	}

	@media (max-width: 600px) {
		.config-actions {
			flex-direction: column;
			align-items: stretch;
		}

		.config-actions .v-btn {
			width: 100%;
			margin-left: 0 !important;
		}
	}

	.advanced-settings {
		background: rgba(0, 0, 0, 0.02);
		border-radius: 4px;
		padding: 8px 12px;
	}

	.advanced-settings .col {
		padding-top: 4px;
		padding-bottom: 0;
	}

	.rtp-max-error::v-deep .v-input__slot fieldset {
		border-color: #ff5252 !important;
		border-width: 2px !important;
	}

	.rtp-max-error::v-deep .v-label {
		color: #ff5252 !important;
	}

	.rtp-max-error::v-deep .v-text-field__details .v-messages__message {
		color: #ff5252 !important;
	}

	/* Custom scrollbar for preview */
	.config-preview-box::-webkit-scrollbar {
		width: 6px;
		height: 6px;
	}

	.config-preview-box::-webkit-scrollbar-track {
		background: rgba(255, 255, 255, 0.1);
	}

	.config-preview-box::-webkit-scrollbar-thumb {
		background: rgba(255, 255, 255, 0.3);
		border-radius: 3px;
	}

	.config-preview-box::-webkit-scrollbar-thumb:hover {
		background: rgba(255, 255, 255, 0.5);
	}

	.rtp-stepper {
		cursor: pointer;
		opacity: 0.6;
		font-size: 14px !important;
		line-height: 1;
	}

	.rtp-stepper:hover {
		opacity: 1;
	}

	/* Hide native number input spinners for RTP field */
	::v-deep input[type="number"]::-webkit-inner-spin-button,
	::v-deep input[type="number"]::-webkit-outer-spin-button {
		-webkit-appearance: none;
		margin: 0;
	}

	::v-deep input[type="number"] {
		-moz-appearance: textfield;
	}
</style>

<style>
	/* Unscoped styles for deep Vuetify overrides */
	.dss-table-scroll .v-simple-table > .v-simple-table__wrapper,
	.dss-table-scroll .v-data-table > .v-data-table__wrapper {
		overflow: visible !important;
	}

	@media (max-width: 600px) {
		.v-dialog.yealink-fullwidth-dialog {
			width: 100% !important;
			max-width: 100% !important;
			margin: 0 !important;
			border-radius: 0 !important;
		}

		/* Make the card fill the fullscreen viewport with a proper sticky-action layout */
		.v-dialog.yealink-fullwidth-dialog > .yealink-dialog-card {
			display: flex;
			flex-direction: column;
			min-height: 100vh;
			border-radius: 0 !important;
		}

		.v-dialog.yealink-fullwidth-dialog > .yealink-dialog-card > .v-card__text.yealink-dialog-text {
			flex: 1 1 auto;
			overflow-y: auto;
		}
	}

	/* Vuetify defaults v-card__title to word-break: break-all and white-space: normal,
	   which on narrow screens forces the title text below the icon. Override it. */
	.v-card__title.yealink-dialog-title {
		word-break: normal !important;
		overflow-wrap: anywhere !important;
		flex-wrap: nowrap !important;
	}

	.v-card__title.yealink-dialog-title > .v-icon {
		flex: 0 0 auto;
	}

	.v-card__title.yealink-dialog-title > span {
		flex: 1 1 auto;
		min-width: 0;
		line-height: 1.3;
		white-space: normal;
		word-break: normal;
		overflow-wrap: anywhere;
	}

	.yealink-dialog-btn.v-btn .v-btn__content .v-icon.v-icon--left {
		margin-left: -2px !important;
		margin-right: 4px !important;
	}

	.yealink-dialog-btn.v-btn .v-btn__content .v-icon.v-icon--right {
		margin-left: 4px !important;
		margin-right: -2px !important;
	}

	/* Syntax highlighting for config preview (unscoped for v-html) */
	.config-preview-box .cfg-directive {
		color: #a6e3a1;
	}

	.config-preview-box .cfg-comment {
		color: #a6e3a1;
	}

	.config-preview-box .cfg-key {
		color: #74c7ec;
	}

	.config-preview-box .cfg-equals {
		color: #ffffff;
	}

	.config-preview-box .cfg-value {
		color: #ffffff;
	}

	.config-preview-box .cfg-content {
		display: inline-block;
		min-width: 100%;
		box-sizing: border-box;
	}

	.config-preview-box .cfg-line {
		padding: 1px 8px 1px 12px;
		border-left: 3px solid transparent;
		min-height: 1.5em;
		display: block;
		box-sizing: border-box;
	}

	.config-preview-box .cfg-line-added {
		background: rgba(0, 230, 118, 0.15);
		border-left-color: #00e676;
	}

	.config-preview-box .cfg-line-modified {
		background: rgba(255, 196, 0, 0.15);
		border-left-color: #ffc400;
	}

	.config-preview-box .cfg-line-unchanged {
		opacity: 0.5;
	}

	.config-preview-box .cfg-line-removed {
		background: rgba(255, 82, 82, 0.15);
		border-left-color: #ff5252;
	}

	.config-preview-box .cfg-old-value {
		color: #a6adc8;
		font-size: 10px;
		font-style: italic;
	}
</style>
